import React from 'react';
import { useSearchParams } from 'react-router-dom';

import styles from './stats.module.scss';
import { useGetStatsAnalyse } from '~/hooks/stats';
import Loader from '~/components/Loader';
import HorizontalBarsTile from '~/components/HorizontalBarsTile';
import StackedHorizontalBarsTile from '~/components/StackedHorizontalBarsTile';

const visitorLegendConfig = [
  {
    key: 'fournisseurs',
    label: 'Fournisseurs',
    dotClass: styles.dotFournisseurs,
    barColor: '#FDC43A',
    barColorMuted: 'rgba(253, 196, 58, 0.35)',
  },
  {
    key: 'magasins',
    label: 'Magasins',
    dotClass: styles.dotMagasins,
    barColor: '#F7E2DF',
    barColorMuted: 'rgba(247, 226, 223, 0.55)',
  },
  {
    key: 'collaborateurs',
    label: 'Collaborateurs',
    dotClass: styles.dotCollaborateurs,
    barColor: '#5DBCB1',
    barColorMuted: 'rgba(93, 188, 177, 0.35)',
  },
] as const;

const StatistiquesAnalyse = () => {
  const [searchParams] = useSearchParams();
  const region = searchParams.get('region') || undefined;

  const {
    data,
    isLoading,
    isFetching,
    error,
  } = useGetStatsAnalyse({ region });

  if (isLoading || isFetching) {
    return (
      <div className={styles.pageLoader}>
        <Loader color="#007D92" />
      </div>
    );
  }

  if (error) {
    return <p>Erreur de chargement.</p>;
  }

  const current = data?.visitorTypeDistribution?.currentYear;
  const previous = data?.visitorTypeDistribution?.previousYear;
  const currentYearLabel = current?.year ?? 2026;
  const previousYearLabel = 'n-1';

  const values = visitorLegendConfig.flatMap((it) => ([
    (current as any)?.[it.key] ?? 0,
    (previous as any)?.[it.key] ?? 0,
  ]));
  const maxValue = Math.max(1, ...values);

  const uniqueCurrent = data?.uniqueVisitors?.currentYear;
  const uniquePrevious = data?.uniqueVisitors?.previousYear;
  const uniqueEvolutionPct = data?.uniqueVisitors?.evolutionPct ?? 0;
  const uniqueEvolutionLabel = uniqueEvolutionPct > 0
    ? `+${uniqueEvolutionPct}%`
    : `${uniqueEvolutionPct}%`;
  const uniqueEvolutionClass = uniqueEvolutionPct >= 0
    ? styles.deltaPositive
    : styles.deltaNegative;

  const lunchCurrent = data?.lunchVisitors?.currentYear;
  const supplierExhibitions = data?.supplierExhibitions || [];
  const supplierPrimary = supplierExhibitions[0] || { label: '', value: 0 };
  const supplierSecondary = supplierExhibitions[1] || { label: '', value: 0 };
  const supplierTotal = Math.max(1, supplierPrimary.value + supplierSecondary.value);

  const conversionCurrent = data?.conversion?.currentYear;
  const conversionEvolutionPct = data?.conversion?.evolutionPct ?? 0;
  const conversionEvolutionLabel = conversionEvolutionPct > 0
    ? `+${conversionEvolutionPct}%`
    : `${conversionEvolutionPct}%`;
  const conversionEvolutionClass = conversionEvolutionPct >= 0
    ? styles.deltaPositive
    : styles.deltaNegative;
  const conversionPct = Math.max(0, Math.min(100, conversionCurrent?.conversionPct ?? 0));

  const functionsItems = (data?.repartitionParFonctions || []).map((item) => ({
    label: item.label,
    value: item.value,
  }));

  const enseigneItems = (data?.repartitionParEnseigne || []).map((item) => ({
    label: item.label,
    value: item.value,
  }));

  const repartitionParRegions = data?.repartitionParRegions || [];

  return (
    <div className={styles.analyseTilesGrid}>
      <section className={styles.tile}>
        <h2>Répartition par type de visiteurs</h2>
        <div className={styles.tileBody}>
          <p className={styles.visitorTypeTileSubtitle}>(nb de total de personnes)</p>

          <div className={styles.visitorTypeChart}>
            {visitorLegendConfig.map((item) => {
              const v2026 = (current as any)?.[item.key] ?? 0;
              const vN1 = (previous as any)?.[item.key] ?? 0;
              const h2026 = Math.round((v2026 / maxValue) * 100);
              const hN1 = Math.round((vN1 / maxValue) * 100);

              return (
                <div key={item.key} className={styles.visitorTypeGroup}>
                  <div className={styles.visitorTypeBarColumn}>
                    <span className={styles.visitorTypeValue}>{v2026}</span>
                    <div
                      className={styles.visitorTypeBar}
                      style={{
                        height: `${h2026}%`,
                        minHeight: 6,
                        background: item.barColor,
                      }}
                    />
                    <span className={styles.visitorTypeYear}>{currentYearLabel}</span>
                  </div>

                  <div className={styles.visitorTypeBarColumn}>
                    <span className={`${styles.visitorTypeValue} ${styles.visitorTypeValueMuted}`}>{vN1}</span>
                    <div
                      className={styles.visitorTypeBar}
                      style={{
                        height: `${hN1}%`,
                        minHeight: 6,
                        background: item.barColorMuted,
                      }}
                    />
                    <span className={styles.visitorTypeYear}>{previousYearLabel}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={styles.visitorTypeLegend}>
            {visitorLegendConfig.map((item) => (
              <span key={item.key} className={styles.visitorTypeLegendItem}>
                <span className={`${styles.visitorTypeDot} ${item.dotClass}`} />
                {item.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.tile}>
        <h2>Nombre de visiteurs uniques</h2>
        <div className={styles.tileBody}>
          <div className={styles.uniqueVisitorsHeader}>
            <span className={styles.uniqueVisitorsTotal}>{uniqueCurrent?.total ?? 0}</span>
            <span className={`${styles.uniqueVisitorsDelta} ${uniqueEvolutionClass}`}>
              {uniqueEvolutionLabel}
            </span>
          </div>

          <div className={styles.uniqueVisitorsSub}>
            <span>Visiteurs au total </span>
            <span className={styles.uniqueVisitorsSubMuted}>
              {uniquePrevious?.total ?? 0} en N-1
            </span>
          </div>

          <div className={styles.uniqueVisitorsRows}>
            <div className={styles.uniqueVisitorsRow}>
              <span className={styles.pillJ1}>J1 uniquement</span>
              <span className={styles.uniqueVisitorsRowValue}>{uniqueCurrent?.j1Only ?? 0}</span>
              <span className={`${styles.uniqueVisitorsRowLabel} ${styles.uniqueVisitorsRowLabelBold}`}>
                Visiteurs
              </span>
            </div>
            <div className={styles.uniqueVisitorsRow}>
              <span className={styles.pillJ2}>J2 uniquement</span>
              <span className={styles.uniqueVisitorsRowValue}>{uniqueCurrent?.j2Only ?? 0}</span>
              <span className={`${styles.uniqueVisitorsRowLabel} ${styles.uniqueVisitorsRowLabelBold}`}>
                Visiteurs
              </span>
            </div>
            <div className={styles.uniqueVisitorsRow}>
              <span className={styles.pillJ1J2}>J1 + J2</span>
              <span className={styles.uniqueVisitorsRowValue}>{uniqueCurrent?.j1j2 ?? 0}</span>
              <span className={`${styles.uniqueVisitorsRowLabel} ${styles.uniqueVisitorsRowLabelBold}`}>
                Visiteurs
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className={styles.analyseStack}>
        <section className={styles.tile}>
          <h2>Inscrits à la restauration</h2>
          <div className={styles.tileBody}>
            <div className={styles.lunchTile}>
              <div className={styles.lunchTileRows}>
                <div className={styles.lunchTileRow}>
                  <span className={styles.pillJ1}>J1</span>
                  <span className={styles.lunchTileRowValue}>{lunchCurrent?.j1 ?? 0}</span>
                </div>
                <div className={styles.lunchTileRow}>
                  <span className={styles.pillJ2}>J2</span>
                  <span className={styles.lunchTileRowValue}>{lunchCurrent?.j2 ?? 0}</span>
                </div>
              </div>

              <div className={styles.lunchTileTotal}>
                <div className={styles.lunchTileTotalValue}>{lunchCurrent?.total ?? 0}</div>
                <div className={styles.lunchTileTotalLabel}>Visiteurs au total</div>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.tile}>
          <h2>Répartition fournisseurs</h2>
          <div className={styles.tileBody}>
            <div className={styles.supplierExpoBar}>
              <div
                className={styles.supplierExpoSegmentPrimary}
                style={{ flexGrow: supplierPrimary.value / supplierTotal }}
              >
                {supplierPrimary.value}
              </div>
              <div
                className={styles.supplierExpoSegmentSecondary}
                style={{ flexGrow: supplierSecondary.value / supplierTotal }}
              >
                {supplierSecondary.value}
              </div>
            </div>
            <div className={styles.supplierExpoLabels}>
              <span>{supplierPrimary.label}</span>
              <span>{supplierSecondary.label}</span>
            </div>
          </div>
        </section>
      </div>

      <section className={styles.tileWide}>
        <h2>Taux de conversion</h2>
        <div className={styles.tileBody}>
          <div className={styles.conversionTile}>
            <div className={styles.conversionDonutWrap}>
              <div className={styles.conversionDonut} style={{ ['--pct' as any]: `${conversionPct}%` }}>
                <div className={styles.conversionDonutInner}>
                  <div className={styles.conversionPct}>{conversionPct}%</div>
                  <div className={`${styles.conversionDelta} ${conversionEvolutionClass}`}>{conversionEvolutionLabel}</div>
                </div>
              </div>
            </div>

            <div className={styles.conversionSide}>
              <div className={styles.conversionLine}>
                <div className={styles.conversionLineBarLight} />
                <div>
                  <div className={styles.conversionLineValue}>
                    {conversionCurrent?.inscriptionsTotal ?? 0}
                  </div>
                  <div className={styles.conversionLineLabel}>inscriptions</div>
                  <div className={styles.conversionLineText}>
                    dont{' '}
                    <span className={styles.conversionLineTextStrong}>
                      {conversionCurrent?.inscriptionsLunch ?? 0}
                    </span>{' '}
                    inscrits au repas
                    <br />
                    soit {conversionCurrent?.inscriptionsLunchPct ?? 0}%
                  </div>
                </div>
              </div>

              <div className={styles.conversionLine}>
                <div className={styles.conversionLineBarDark} />
                <div>
                  <div className={styles.conversionLineValue}>
                    {conversionCurrent?.presenceTotal ?? 0}
                  </div>
                  <div className={styles.conversionLineLabel}>Présence</div>
                  <div className={styles.conversionLineText}>
                    dont{' '}
                    <span className={styles.conversionLineTextStrong}>
                      {conversionCurrent?.presenceLunch ?? 0}
                    </span>{' '}
                    inscrits au repas
                    <br />
                    soit {conversionCurrent?.presenceLunchPct ?? 0}%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className={styles.analyseTileEmbed}>
        <HorizontalBarsTile
          title="Répartition par fonctions"
          items={functionsItems}
          barColor="#007D8F"
        />
      </div>

      <div className={styles.analyseTileEmbed}>
        <HorizontalBarsTile
          title="Répartition par enseigne"
          items={enseigneItems}
          barColor="#007D8F"
        />
      </div>

      <div className={styles.analyseTileEmbed}>
        <StackedHorizontalBarsTile
          title="Répartition par régions et enseignes"
          items={repartitionParRegions}
        />
      </div>
    </div>
  );
};

export default StatistiquesAnalyse;
